import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Card, CardTitle, CardDescription } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Toggle } from '@/components/ui/Toggle';
import { Button } from '@/components/ui/Button';
import { Chip } from '@/components/ui/Chip';
import { useCompanyInfo, useUpdateHermesConfig } from '@/hooks/useSettings';
import { Webhook, Server, Shield, AlertCircle, ExternalLink } from 'lucide-react';

interface FormValues {
  webhookUrl: string;
  apiKey: string;
  hmacSecret: string;
  apiKeyId: string;
  enabled: boolean;
}

export function HermesConfigForm() {
  const { data: company, isLoading } = useCompanyInfo();
  const update = useUpdateHermesConfig();

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isDirty },
  } = useForm<FormValues>({
    defaultValues: {
      webhookUrl: '',
      apiKey: '',
      hmacSecret: '',
      apiKeyId: '',
      enabled: false,
    },
  });

  useEffect(() => {
    if (company) {
      reset({
        webhookUrl: company.hermes?.webhookUrl ?? '',
        apiKey: company.hermes?.apiKey ?? '',
        hmacSecret: company.hermes?.hmacSecret ?? '',
        apiKeyId: company.hermes?.apiKeyId ?? '',
        enabled: company.hermes?.enabled ?? false,
      });
    }
  }, [company, reset]);

  const onSubmit = async (values: FormValues) => {
    await update.mutateAsync({
      webhookUrl: values.webhookUrl.trim(),
      apiKey: values.apiKey.trim() || undefined,
      hmacSecret: values.hmacSecret.trim() || undefined,
      apiKeyId: values.apiKeyId.trim() || undefined,
      enabled: values.enabled,
    });
  };

  const enabled = watch('enabled');
  const webhookUrl = watch('webhookUrl');
  const hmacSecret = watch('hmacSecret');

  return (
    <Card>
      <div className="flex items-start gap-3 mb-5">
        <div className="w-10 h-10 rounded bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
          <Server size={18} />
        </div>
        <div className="flex-1">
          <CardTitle>Hermes WhatsApp Gateway</CardTitle>
          <CardDescription>
            Self-hosted Baileys gateway di VPS kamu sendiri. Bebas biaya per
            pesan, full control. Lihat{' '}
            <a
              href="https://github.com/your-org/hermes-agent"
              target="_blank"
              rel="noreferrer"
              className="text-primary inline-flex items-center gap-0.5 hover:underline"
            >
              HERMES_INTEGRATION.md <ExternalLink size={11} />
            </a>{' '}
            untuk setup.
          </CardDescription>
        </div>
      </div>

      <div className="mb-4 flex items-center gap-2">
        <Chip variant={enabled ? 'success' : 'default'}>
          {enabled ? 'Aktif' : 'Nonaktif'}
        </Chip>
        <span className="text-caption text-ink-subtle">
          {enabled
            ? 'Invoice baru akan dikirim via Hermes (self-hosted)'
            : 'Invoice tidak akan dikirim otomatis via Hermes'}
        </span>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Toggle
          checked={enabled}
          onChange={(v) => setValue('enabled', v, { shouldDirty: true })}
          label="Aktifkan Hermes"
          description="Hanya aktifkan setelah Hermes running dan webhook URL siap menerima."
        />

        <Input
          label="Webhook URL"
          type="url"
          placeholder="https://hermes.yourdomain.com/webhook/ophthpos"
          hint="HTTPS endpoint. Lihat HERMES_SETUP.md untuk setup VPS Contabo + Caddy."
          error={errors.webhookUrl?.message}
          {...register('webhookUrl', {
            validate: (v) => {
              if (enabled && !v.trim()) return 'Webhook URL wajib diisi kalau aktif';
              if (v.trim() && !v.startsWith('https://')) return 'Harus https://';
              return true;
            },
          })}
          disabled={isLoading}
        />

        <Input
          label="API Key"
          type="password"
          placeholder="Bearer token dari Hermes (.env: OPHTH_API_KEY)"
          hint="Disimpan terenkripsi di Firestore. Dikirim sebagai Authorization: Bearer header."
          {...register('apiKey')}
        />

        <details className="rounded border border-line bg-canvas/30 px-3 py-2">
          <summary className="text-small font-medium text-ink cursor-pointer flex items-center gap-1.5 select-none">
            <Shield size={14} className="text-primary" />
            Advanced: HMAC signature & multi-tenant
          </summary>
          <div className="mt-3 space-y-3">
            <Input
              label="HMAC Secret (opsional, recommended)"
              type="password"
              placeholder="Shared secret dari Hermes (.env: OPHTH_HMAC_SECRET)"
              hint={
                hmacSecret
                  ? 'Signature aktif — request akan include X-Ophth-Signature header'
                  : 'Kosongkan kalau Hermes gak pakai signature verification'
              }
              {...register('hmacSecret')}
            />
            <Input
              label="API Key ID (opsional)"
              placeholder="ophthpos_default"
              hint="Untuk multi-tenant. Kosongkan kalau single-tenant."
              {...register('apiKeyId')}
            />
          </div>
        </details>

        {enabled && !webhookUrl && (
          <div className="flex items-start gap-2 p-3 rounded bg-warning/5 border border-warning/30 text-small text-warning">
            <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
            <span>Webhook URL belum diisi. Invoice tidak akan terkirim sampai ini diisi.</span>
          </div>
        )}

        {company?.openclaw?.enabled && (
          <div className="flex items-start gap-2 p-3 rounded bg-info/5 border border-info/30 text-small text-info">
            <Webhook size={16} className="mt-0.5 flex-shrink-0" />
            <span>
              OpenClaw juga aktif. Hermes akan di-prefer — OpenClaw jadi
              fallback kalau Hermes gagal. Disable OpenClaw setelah migrasi
              selesai.
            </span>
          </div>
        )}

        <div className="pt-2 flex justify-end">
          <Button
            type="submit"
            variant="primary"
            loading={update.isPending}
            disabled={!isDirty}
          >
            Simpan konfigurasi
          </Button>
        </div>
      </form>
    </Card>
  );
}
